import { gql } from '@apollo/client';

import { loggedUserData, invitationData } from './fragments';

export const SIGN_IN = gql`
  mutation SignIn($token: String!) {
    signIn(token: $token) {
      code
      success
      error
      needsLinking
      conflictingMethod
      maskedContact
      user {
        ...LoggedUserData
      }
    }
  }
  ${loggedUserData}
`;

export const SIGN_IN_WEB = gql`
  mutation SignInWeb($token: String!, $extraData: JSON!) {
    signInWeb(token: $token, extraData: $extraData) {
      code
      success
      error
      needsLinking
      conflictingMethod
      maskedContact
      user {
        ...LoggedUserData
      }
    }
  }
  ${loggedUserData}
`;

// A texted Archive check-in link's key, traded for a session that only
// answers interviews (proxi-api services/auth/interviewLinkSession.ts).
export const SIGN_IN_WITH_INTERVIEW_LINK = gql`
  mutation SignInWithInterviewLink($interviewId: String!, $key: String!) {
    signInWithInterviewLink(interviewId: $interviewId, key: $key) {
      code
      success
      error
      customToken
    }
  }
`;

// A texted Archive group interview invite's key (/invite/<token>?k=…): a
// session for an invitee with a login, or needsRegistration (and the number to
// sign up with) for one without.
export const SIGN_IN_WITH_INVITE_LINK = gql`
  mutation SignInWithInviteLink($token: String!, $key: String!) {
    signInWithInviteLink(token: $token, key: $key) {
      code
      success
      error
      customToken
      needsRegistration
      phone
    }
  }
`;

export const LINK_ACCOUNT = gql`
  mutation LinkAccount($token: String!, $extraData: JSON!) {
    linkAccount(token: $token, extraData: $extraData) {
      code
      success
      error
      user {
        ...LoggedUserData
      }
    }
  }
  ${loggedUserData}
`;

export const GET_NEXT_MESSAGES_INTERVIEW = gql`
  mutation GetNextMessagesInterview($input: GetNextMessagesInput) {
    getNextMessagesInterview(input: $input) {
      code
      success
      error
      messages {
        _id
        text
        audioUrl
        questionAudioUrl
        questionAudioTimings {
          word
          startMs
          endMs
        }
        formKey
        userResponseType
        game {
          key
          name
          step
          round
          totalRounds
          title
          choices
          dice
          year
          age
          wantsPhoto
          score
          bonusCredits
          actions {
            label
            reply
          }
          results {
            title
            winner
            votes
            total
          }
        }
        media {
          circleId
          sourceType
          sourceId
          mediaType
          contentType
          name
          start
          end
          requestId
          excerpt
          resume
        }
        mediaIdResult {
          requestId
          chips {
            people
            what
            when
            where
            alsoThere
          }
        }
      }
      chat {
        _id
        chatType
        status
      }
      components {
        prompts {
          _id
          name
          system
          user
          settings
        }
        hardCodedMessages {
          _id
          name
          copy
          copyOptions
          userResponseType
        }
        baseQuestions {
          _id
          text
          followUpText
          status
        }
      }
      focus {
        _id
        userText
      }
      category {
        _id
        name
        shortSessionSummary
        shortSessionSummaryAudio
        isOpenInterview
        openInterviewGoal
        openInterviewTimeLimit
        openInterviewMaxQuestions
      }
      user {
        ...LoggedUserData
      }
      voiceId
      isOpenInterview
      openInterviewStatus {
        isActive
        timeRemainingSeconds
        questionsAsked
        maxQuestions
        goalAchieved
        goalProgress
        shouldTerminate
        terminationReason
      }
      creditsEarned
      creditsBalance
    }
  }
  ${loggedUserData}
`;

export const PROCESS_MESSAGE = gql`
  mutation ProcessMessage($messageId: String!, $audioUrl: String!) {
    processMessage(messageId: $messageId, audioUrl: $audioUrl)
  }
`;

export const RESET_WEB_TRAIN_CHATS = gql`
  mutation ResetWebTrainChats {
    resetWebTrainChats
  }
`;

export const ACCEPT_INVITATION = gql`
  mutation AcceptInvitation($token: String!) {
    acceptInvitation(token: $token) {
      success
      message
      statusCode
      invitation {
        ...InvitationData
      }
    }
  }
  ${invitationData}
`;

export const DECLINE_INVITATION = gql`
  mutation DeclineInvitation($token: String!) {
    declineInvitation(token: $token) {
      success
      message
      statusCode
      invitation {
        ...InvitationData
      }
    }
  }
  ${invitationData}
`;

export const RESEND_INVITATION = gql`
  mutation ResendInvitation($invitationId: String!) {
    resendInvitation(invitationId: $invitationId) {
      success
      message
      statusCode
    }
  }
`;

export const ACCEPT_CIRCLE_INVITE = gql`
  mutation AcceptCircleInvite(
    $token: String!
    $authToken: String!
    $firstName: String!
    $lastName: String!
    $phone: String
    $preferredChannel: String
    $smsConsent: Boolean
    $whatsappConsent: Boolean
    $email: String
    $emailConsent: Boolean
  ) {
    acceptCircleInvite(
      token: $token
      authToken: $authToken
      firstName: $firstName
      lastName: $lastName
      phone: $phone
      preferredChannel: $preferredChannel
      smsConsent: $smsConsent
      whatsappConsent: $whatsappConsent
      email: $email
      emailConsent: $emailConsent
    ) {
      success
      message
      statusCode
      invitation {
        ...InvitationData
      }
      user {
        ...LoggedUserData
      }
    }
  }
  ${invitationData}
  ${loggedUserData}
`;

export const ACCEPT_GLOBAL_LINK_INVITATION = gql`
  mutation AcceptGlobalLinkInvitation($input: AcceptGlobalLinkInvitationInput!) {
    acceptGlobalLinkInvitation(input: $input) {
      success
      message
      interview
      category
    }
  }
`;

export const GET_SIGNED_UPLOAD_URL = gql`
  mutation GetSignedUploadUrl($fileName: String!, $contentType: String!) {
    getSignedUploadUrl(fileName: $fileName, contentType: $contentType)
  }
`;

export const PRESIGN_WEB_TRAIN_VIDEO_UPLOAD = gql`
  mutation PresignWebTrainVideoUpload($input: PresignWebTrainVideoInput!) {
    presignWebTrainVideoUpload(input: $input) {
      code
      success
      error
      uploadUrl
      s3Key
      publicUrl
    }
  }
`;

// Interview Attachment mutations
export const INTERVIEWER_ATTACHMENT_PRESIGN = gql`
  mutation InterviewerAttachmentPresign($input: InterviewerAttachmentPresignInput!) {
    interviewerAttachmentPresign(input: $input) {
      code
      success
      error
      uploadUrl
      s3Key
    }
  }
`;

export const SAVE_INTERVIEWER_ATTACHMENT = gql`
  mutation SaveInterviewerAttachment($input: SaveInterviewerAttachmentInput!) {
    saveInterviewerAttachment(input: $input) {
      code
      success
      error
      attachment {
        _id
        fileName
        s3Key
        contentType
        fileSize
        uploadedAt
        requestKey
      }
    }
  }
`;

export const SAVE_PERSONAL_DATA = gql`
  mutation SavePersonalData($fields: JSON!) {
    savePersonalData(fields: $fields) {
      code
      success
      error
      user {
        ...LoggedUserData
      }
    }
  }
  ${loggedUserData}
`;

export const UPDATE_ZIP_CODE = gql`
  mutation UpdateZipCode($zipCode: String!) {
    updateZipCode(zipCode: $zipCode) {
      code
      success
      error
      user {
        ...LoggedUserData
      }
    }
  }
  ${loggedUserData}
`;

export const ANALYZE_PROFILE_PHOTO = gql`
  mutation AnalyzeProfilePhoto($imageUrl: String!) {
    analyzeProfilePhoto(imageUrl: $imageUrl) {
      success
      approved
      feedback
    }
  }
`;

export const SEND_SET_PASSWORD_EMAIL = gql`
  mutation SendSetPasswordEmail($email: String!) {
    sendSetPasswordEmail(email: $email) {
      success
    }
  }
`;
